import { Property } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Edit, Trash2, Plus, AlertCircle, RefreshCw } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { useLanguage } from "@/lib/language";

interface PropertyListProps {
  properties: Property[];
  loading: boolean;
  error: Error | null;
  onEdit: (property: Property) => void;
  onDelete: (propertyId: string) => void;
  onCreateNew: () => void;
}

export function PropertyList({
  properties,
  loading,
  error,
  onEdit,
  onDelete,
  onCreateNew,
}: PropertyListProps) {
  const { t } = useLanguage();
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">{t("loadingProperties")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          {t("errorLoadingProperties")}: {error.message}
        </AlertDescription>
      </Alert>
    );
  }

  if (properties.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto max-w-md">
          <div className="mx-auto h-12 w-12 text-muted-foreground mb-4">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t("noPropertiesFound")}
          </h3>
          <p className="text-muted-foreground mb-6">{t("getStartedMessage")}</p>
          <Button onClick={onCreateNew} className="flex items-center space-x-2">
            <Plus className="h-4 w-4" />
            <span>{t("addFirstProperty")}</span>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
            {t("properties")}
          </h2>
          <p className="text-muted-foreground text-sm sm:text-base">
            {t("managePropertyListings")} ({properties.length} {t("total")})
          </p>
        </div>
        <Button
          onClick={onCreateNew}
          className="flex items-center space-x-2 w-full sm:w-auto"
        >
          <Plus className="h-4 w-4" />
          <span>{t("addProperty")}</span>
        </Button>
      </div>

      {/* Mobile Card View */}
      <div className="grid gap-4 md:hidden">
        {properties.map((property) => (
          <Card key={property.id} className="overflow-hidden">
            <CardContent className="p-4">
              <div className="flex items-start space-x-3">
                {property.images[0] && (
                  <img
                    src={property.images[0]}
                    alt={property.title.en}
                    className="h-16 w-16 rounded-lg object-cover flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate">
                        {property.title.en}
                      </h3>
                      <p className="text-sm text-muted-foreground truncate">
                        {property.location.city.en},{" "}
                        {property.location.neighborhood.en}
                      </p>
                    </div>
                    <div className="flex space-x-1 ml-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(property)}
                        className="p-2"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDelete(property.id)}
                        className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <Badge
                      variant={
                        property.type === "rent" ? "secondary" : "default"
                      }
                      className="text-xs"
                    >
                      {property.type === "rent" ? t("forRent") : t("forSale")}
                    </Badge>
                    <Badge
                      variant={
                        property.status === "available"
                          ? "default"
                          : property.status === "sold"
                          ? "destructive"
                          : "secondary"
                      }
                      className="text-xs"
                    >
                      {property.status === "available"
                        ? t("available")
                        : property.status === "sold"
                        ? t("sold")
                        : t("rented")}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between mt-3 text-sm">
                    <span className="font-medium">
                      {formatCurrency(property.price, property.currency)}
                    </span>
                    <div className="flex items-center space-x-3 text-muted-foreground">
                      <span>
                        {property.rooms} {t("rooms")}
                      </span>
                      <span>{property.size} m²</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Desktop Table View */}
      <Card className="hidden md:block">
        <CardHeader>
          <CardTitle>{t("propertyListings")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("property")}</TableHead>
                  <TableHead>{t("location")}</TableHead>
                  <TableHead>{t("type")}</TableHead>
                  <TableHead>{t("price")}</TableHead>
                  <TableHead>{t("status")}</TableHead>
                  <TableHead>{t("rooms")}</TableHead>
                  <TableHead>{t("size")}</TableHead>
                  <TableHead className="text-right">{t("actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {properties.map((property) => (
                  <TableRow key={property.id}>
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        {property.images[0] && (
                          <img
                            src={property.images[0]}
                            alt={property.title.en}
                            className="h-12 w-12 rounded-lg object-cover"
                          />
                        )}
                        <div>
                          <p className="font-medium text-gray-900 truncate max-w-xs">
                            {property.title.en}
                          </p>
                          <p className="text-sm text-muted-foreground truncate max-w-xs">
                            {property.description.en}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p className="font-medium">
                          {property.location.city.en}
                        </p>
                        <p className="text-muted-foreground">
                          {property.location.neighborhood.en}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          property.type === "rent" ? "secondary" : "default"
                        }
                      >
                        {property.type === "rent" ? t("forRent") : t("forSale")}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">
                        {formatCurrency(property.price, property.currency)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          property.status === "available"
                            ? "default"
                            : property.status === "sold"
                            ? "destructive"
                            : "secondary"
                        }
                      >
                        {property.status === "available"
                          ? t("available")
                          : property.status === "sold"
                          ? t("sold")
                          : t("rented")}
                      </Badge>
                    </TableCell>
                    <TableCell>{property.rooms}</TableCell>
                    <TableCell>{property.size} m²</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEdit(property)}
                          className="flex items-center space-x-1"
                        >
                          <Edit className="h-4 w-4" />
                          <span>{t("edit")}</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDelete(property.id)}
                          className="flex items-center space-x-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                          <span>{t("delete")}</span>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
